import type React from "react";
import { useEffect } from "react";
import {
  Button,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormDescription,
  FormDialog,
  FormDialogCancel,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from "@capacit-com/lara-law-shared-ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";

const formSchema = z.object({
  canonicalTerm: z.string().trim().min(1, { message: "Udfyld kanonisk term" }),
});

type FormValues = z.infer<typeof formSchema>;

type RenameSynonymGroupDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentCanonicalTerm: string;
  onRename: (newCanonicalTerm: string) => void;
};

const RenameSynonymGroupDialog: React.FC<RenameSynonymGroupDialogProps> = ({
  open,
  onOpenChange,
  currentCanonicalTerm,
  onRename,
}) => {
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { canonicalTerm: currentCanonicalTerm },
  });

  // Re-seed whenever a different group is picked — the dialog stays mounted.
  useEffect(() => {
    if (open) form.reset({ canonicalTerm: currentCanonicalTerm });
  }, [open, currentCanonicalTerm, form]);

  const canonicalTerm = form.watch("canonicalTerm");
  const isUnchanged = canonicalTerm.trim() === currentCanonicalTerm;

  const handleSubmit = form.handleSubmit((values) => {
    if (values.canonicalTerm !== currentCanonicalTerm) {
      onRename(values.canonicalTerm);
    }
  });

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      isDirty={form.formState.isDirty}
    >
      <DialogHeader>
        <DialogTitle>Rediger kanonisk term</DialogTitle>
        <DialogDescription>
          Opdater den kanoniske term som alle synonymer i denne gruppe mapper
          til
        </DialogDescription>
      </DialogHeader>
      <Form {...form}>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <FormField
              control={form.control}
              name="canonicalTerm"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Kanonisk term</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="f.eks., smartphone" />
                  </FormControl>
                  <FormDescription>
                    Dette vil ændre den term som alle synonymer mapper til i
                    søgeresultater
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <DialogFooter>
            <FormDialogCancel />
            <Button
              type="submit"
              disabled={!canonicalTerm.trim() || isUnchanged}
            >
              Gem ændringer
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </FormDialog>
  );
};

export default RenameSynonymGroupDialog;
